import React, { useState } from 'react';
import { useWizard } from '../../../context/WizardContext';
import { BarChart3, MessageSquare, ChevronDown, ChevronUp, Lightbulb, ExternalLink } from 'lucide-react';
import './AnalysisReminder.css';

const AnalysisReminder = () => {
  const { dataB } = useWizard();
  const [isExpanded, setIsExpanded] = useState(true);

  const analysisApproach = dataB?.analysisApproach;
  const qualMethodsMap = dataB?.qualitativeMethods || {};
  const quantMethods = dataB?.quantitativeMethods || {};
  const descriptiveCategories = quantMethods.descriptiveStats?.categories || [];
  const inferentialTests = quantMethods.inferentialTests?.tests || [];

  const methodKeyMap = {
    thematicAnalysis: 'thematic',
    contentAnalysis: 'content',
    sentimentAnalysis: 'sentiment',
    narrativeAnalysis: 'narrative',
    discourseAnalysis: 'discourse',
    groundedTheory: 'grounded',
    phenomenologicalAnalysis: 'phenomenological'
  };

  const qualMethods = Object.entries(qualMethodsMap)
    .filter(([key, value]) => value === true && methodKeyMap[key])
    .map(([key]) => methodKeyMap[key]);

  const hasQualitative = analysisApproach === 'qualitative' || analysisApproach === 'mixed';
  const hasQuantitative = analysisApproach === 'quantitative' || analysisApproach === 'mixed';

  const hasAnalysisPlan = qualMethods.length > 0 || descriptiveCategories.length > 0 || inferentialTests.length > 0;

  if (!hasAnalysisPlan) {
    return null;
  }

  const qualMethodLabels = {
    thematic: 'Thematic Analysis',
    content: 'Content Analysis',
    sentiment: 'Sentiment Analysis',
    narrative: 'Narrative Analysis',
    discourse: 'Discourse Analysis',
    grounded: 'Grounded Theory',
    phenomenological: 'Phenomenological Analysis'
  };

  const descriptiveCategoryLabels = {
    central_tendency: 'Central Tendency',
    dispersion: 'Dispersion & Spread',
    distribution: 'Distribution Shape',
    frequency: 'Frequency Analysis',
    position: 'Position & Percentiles',
    outliers: 'Outlier Detection',
    correlation: 'Correlation Summary',
    categorical: 'Categorical Summary'
  };

  const getQuestionTypeRecommendations = () => {
    const recommendations = [];

    if (qualMethods.includes('sentiment')) {
      recommendations.push({ type: 'Open-ended text', reason: 'for sentiment analysis' });
    }
    if (qualMethods.includes('thematic') || qualMethods.includes('content')) {
      recommendations.push({ type: 'Open-ended text', reason: 'for thematic/content analysis' });
    }

    if (inferentialTests.some(t => ['independent_t', 'paired_t', 'one_way_anova', 'repeated_anova', 'mann_whitney', 'wilcoxon', 'kruskal_wallis', 'friedman'].includes(t))) {
      recommendations.push({ type: 'Likert Scale or Number Input', reason: 'for group comparison tests' });
    }
    if (inferentialTests.some(t => ['pearson', 'spearman', 'kendall', 'linear_regression', 'multiple_regression'].includes(t))) {
      recommendations.push({ type: 'Likert Scale or Number Input', reason: 'for correlation/regression analysis' });
    }
    if (inferentialTests.some(t => ['chi_square', 'fisher_exact', 'mcnemar'].includes(t))) {
      recommendations.push({ type: 'Multiple Choice or Dropdown', reason: 'for categorical analysis' });
    }
    if (descriptiveCategories.includes('frequency') || descriptiveCategories.includes('categorical')) {
      recommendations.push({ type: 'Multiple Choice or Checkbox', reason: 'for frequency analysis' });
    }

    const uniqueRecommendations = [];
    const seen = new Set();
    recommendations.forEach(r => {
      if (!seen.has(r.type)) {
        seen.add(r.type);
        uniqueRecommendations.push(r);
      }
    });

    return uniqueRecommendations;
  };

  const recommendations = getQuestionTypeRecommendations();

  return (
    <div className="analysis-reminder">
      <div 
        className="reminder-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="reminder-title">
          <Lightbulb size={18} />
          <span>Your Data Analysis Plan</span>
        </div>
        <button className="toggle-btn">
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>

      {isExpanded && (
        <div className="reminder-content">
          <p className="reminder-intro">
            Build your survey to collect data that matches your planned analysis methods:
          </p>

          {hasQualitative && qualMethods.length > 0 && (
            <div className="reminder-section">
              <div className="section-header">
                <MessageSquare size={14} />
                <span>Qualitative Methods</span>
              </div>
              <div className="method-tags">
                {qualMethods.map(method => (
                  <span key={method} className="method-tag qual">
                    {qualMethodLabels[method] || method}
                  </span>
                ))}
              </div>
            </div>
          )}

          {hasQuantitative && (descriptiveCategories.length > 0 || inferentialTests.length > 0) && (
            <div className="reminder-section">
              <div className="section-header">
                <BarChart3 size={14} />
                <span>Quantitative Methods</span>
              </div>
              {descriptiveCategories.length > 0 && (
                <div className="method-group">
                  <span className="group-label">Descriptive:</span>
                  <div className="method-tags">
                    {descriptiveCategories.slice(0, 3).map(cat => (
                      <span key={cat} className="method-tag quant">
                        {descriptiveCategoryLabels[cat] || cat}
                      </span>
                    ))}
                    {descriptiveCategories.length > 3 && (
                      <span className="method-tag more">+{descriptiveCategories.length - 3} more</span>
                    )}
                  </div>
                </div>
              )}
              {inferentialTests.length > 0 && (
                <div className="method-group">
                  <span className="group-label">Inferential:</span>
                  <span className="test-count">{inferentialTests.length} test(s) planned</span>
                </div>
              )}
            </div>
          )}

          {recommendations.length > 0 && (
            <div className="reminder-section recommendations">
              <div className="section-header">
                <ExternalLink size={14} />
                <span>Recommended Question Types</span>
              </div>
              <ul className="recommendation-list">
                {recommendations.map((rec, idx) => (
                  <li key={idx}>
                    <strong>{rec.type}</strong> {rec.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalysisReminder;
